import { useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createCookie } from '@wbk/utils';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { defaultLang, shortLangsMap, supportedLngs } from '@/i18n/config';
import Context from './context';

type Props = {
  children: React.ReactNode;
};

const LocalizationProvider = ({ children }: Props) => {
  const { i18n } = useTranslation('common');
  const { lang } = useParams<{ lang: Language }>();
  const { pathname } = useLocation();
  const validLang = lang && supportedLngs.includes(lang) ? lang : defaultLang;
  const shortLang = shortLangsMap[validLang] || 'en';
  const navigate = useNavigate();

  // Update DOM immediately when language changes
  useLayoutEffect(() => {
    if (shortLang) {
      document.documentElement.lang = shortLang;
      document.documentElement.dir = shortLang.startsWith('ar') ? 'rtl' : 'ltr';
    }
  }, [shortLang]);

  // Initialize i18n language on mount
  useEffect(() => {
    if (shortLang && i18n.language !== shortLang) {
      i18n.changeLanguage(shortLang);
    }
  }, [shortLang, i18n]);

  const updateLocale = async (newLocale: string) => {
    await i18n.changeLanguage(newLocale);
    createCookie({ name: 'lang', value: newLocale, expiration: 30 });
    document.documentElement.lang = newLocale;
    document.documentElement.dir = newLocale.startsWith('ar') ? 'rtl' : 'ltr';
  };

  const setLocale = async (newLocale: string) => {
    const pathWithoutLang = pathname.replace(`/${lang}`, '');
    if (newLocale !== shortLang) {
      const search = new URLSearchParams(window.location.search).toString();
      await updateLocale(newLocale);
      const dest = `/${newLocale}${pathWithoutLang}${search ? `?${search}` : ''}`;
      navigate(dest);
    }
  };

  return (
    <Context.Provider
      value={{
        locale: shortLang,
        setLocale,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default LocalizationProvider;
